import { onScopeDispose, watch } from 'vue'
import { useOnline } from '@vueuse/core'
import { getAppParams, useAppParams } from '@/stores/app'
import { getDeviceListByUserId } from '@/api/device'
import { postMessageToRN } from '@/bridge'

const PUSH_RETRY_DELAYS = [2_000, 4_000, 8_000, 16_000, 32_000, 60_000]
const RETRYABLE_CODES = new Set([408, 502, 504])

let initialized = false
let generation = 0
let pushDone = false // 当前周期已查到新鲜设备列表，或已发出开启指令；不代表原生注册成功
let pushRetryIdx = 0
let pushRetryTimer: ReturnType<typeof setTimeout> | undefined
let inFlight: AbortController | null = null
let nativeLogin: { token: string; ready: boolean } | null = null

function clearPushRetry() {
  if (pushRetryTimer !== undefined) {
    clearTimeout(pushRetryTimer)
    pushRetryTimer = undefined
  }
}

function canStart() {
  return initialized && !!getAppParams().token && (!nativeLogin || nativeLogin.ready)
}

/** 停止当前周期；即使请求已经完成、无法取消，旧回调也会被 generation 隔离。 */
export function stopPushCheck() {
  generation++
  inFlight?.abort()
  inFlight = null
  clearPushRetry()
  pushDone = false
  pushRetryIdx = 0
}

async function checkPush(token: string) {
  const currentGeneration = generation
  const controller = new AbortController()
  inFlight = controller
  const isCurrent = () => currentGeneration === generation && token === getAppParams().token

  try {
    // 保留响应信封，不经 useApi 丢失请求上下文；此查询禁止缓存读写。
    const response = await getDeviceListByUserId({ skipCache: true, signal: controller.signal })
    if (!isCurrent()) return
    if (response.code !== 0) throw response
    if (!Array.isArray(response.data)) return

    pushDone = true
    clearPushRetry()
    if (response.data.some((device: { messagingEnabled?: boolean }) => device.messagingEnabled)) {
      postMessageToRN({ type: 'enablePush' })
    }
  } catch (error) {
    if (!isCurrent() || !RETRYABLE_CODES.has((error as { code?: number })?.code ?? -1)) return
    const delay = PUSH_RETRY_DELAYS[Math.min(pushRetryIdx, PUSH_RETRY_DELAYS.length - 1)]
    pushRetryIdx = Math.min(pushRetryIdx + 1, PUSH_RETRY_DELAYS.length - 1)
    pushRetryTimer = setTimeout(() => {
      pushRetryTimer = undefined
      if (isCurrent()) startPushCheck()
    }, delay)
  } finally {
    // 旧请求完成时不能释放新周期的在途锁。
    if (isCurrent()) inFlight = null
  }
}

/** 普通触发合并在途检查；force 使旧周期失效并重新查询（绑定设备后）。 */
export function startPushCheck(force = false) {
  if (!canStart()) return
  if (force) stopPushCheck()
  if (pushDone || inFlight) return
  clearPushRetry()
  void checkPush(getAppParams().token!)
}

/** 消息页直接请求开启；沿用业务约定绕过 messagingEnabled，但不能绕过登录门槛。 */
export function enablePushNow() {
  if (!canStart()) return
  stopPushCheck()
  pushDone = true
  postMessageToRN({ type: 'enablePush' })
}

/** 写入 H5 token 前调用；原生 userInfo 持久化成功后 complete，旧登录的句柄失效。 */
export function preparePushLogin(token: string) {
  stopPushCheck()
  const login = { token, ready: false }
  nativeLogin = login
  const isCurrent = () => nativeLogin === login && getAppParams().token === token
  return {
    isCurrent,
    complete() {
      if (!isCurrent()) return
      login.ready = true
      startPushCheck()
    },
  }
}

/** App.vue setup 注册一次；冷启动沿用已注入的登录态，新登录等待原生持久化。 */
export function initPushCheck() {
  if (initialized) return
  initialized = true
  const params = useAppParams()
  const isOnline = useOnline()

  watch(() => params.token, (token) => {
    stopPushCheck()
    if (nativeLogin?.token !== token) nativeLogin = null
    startPushCheck()
  }, { immediate: true, flush: 'sync' })

  watch(isOnline, (online) => {
    if (online) startPushCheck()
  })

  onScopeDispose(() => {
    stopPushCheck()
    nativeLogin = null
    initialized = false
  })
}
